import {
    type ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    useMemo
} from "react";
import {
    BLOCK_DICTIONARIES
} from "./locales/blocks";
import {
    COMPONENT_DICTIONARIES
} from "./locales/components";
import {
    CREATE_DICTIONARIES
} from "./locales/create";
import en from "./locales/en.json";
import {
    FOUNDATION_DICTIONARIES
} from "./locales/foundations";
import {
    RELEASE_NOTE_DICTIONARIES
} from "./locales/releaseNotes";
import tr from "./locales/tr.json";
import {
    NCoreUIKitLocalize
} from "ncore-ui-kit";

export type DocsLanguage = "tr" | "en";

type DocsI18nContextType = {
    t: (key: string, args?: Array<string | number>) => string;
    setLanguage: (language: DocsLanguage) => void;
    language: DocsLanguage;
};

const DICTIONARIES: Record<DocsLanguage, Record<string, string>> = {
    en: {
        ...en,
        ...COMPONENT_DICTIONARIES.en,
        ...FOUNDATION_DICTIONARIES.en,
        ...RELEASE_NOTE_DICTIONARIES.en,
        ...BLOCK_DICTIONARIES.en,
        ...CREATE_DICTIONARIES.en
    },
    tr: {
        ...tr,
        ...COMPONENT_DICTIONARIES.tr,
        ...FOUNDATION_DICTIONARIES.tr,
        ...RELEASE_NOTE_DICTIONARIES.tr,
        ...BLOCK_DICTIONARIES.tr,
        ...CREATE_DICTIONARIES.tr
    }
};

const LANGUAGE_STORAGE_KEY = "ncore-docs-lang";

const KIT_LOCALES: Record<DocsLanguage, "tr-TR" | "en-US"> = {
    en: "en-US",
    tr: "tr-TR"
};

export const readStoredLanguage = (): DocsLanguage => {
    try {
        const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);

        if(storedLanguage === "en" || storedLanguage === "tr") {
            return storedLanguage;
        }
    } catch {
        return "tr";
    }

    return "tr";
};

export const getKitLocale = (language: DocsLanguage) => KIT_LOCALES[language];

const DocsI18nContext = createContext<DocsI18nContextType>({
    setLanguage: () => undefined,
    t: (key) => key,
    language: "tr"
});

export const DocsI18nProvider = ({
    initialLanguage,
    children
}: {
    initialLanguage?: DocsLanguage;
    children: ReactNode;
}) => {
    const [
        language,
        setLanguageState
    ] = useState<DocsLanguage>(() => initialLanguage ?? readStoredLanguage());

    useEffect(() => {
        document.documentElement.setAttribute("lang", language);
    }, [
        language
    ]);

    const setLanguage = useCallback((nextLanguage: DocsLanguage) => {
        setLanguageState(nextLanguage);

        NCoreUIKitLocalize.switch({
            localeKey: KIT_LOCALES[nextLanguage]
        });

        try {
            window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
        } catch {
            return;
        }
    }, []);

    const t = useCallback((key: string, args?: Array<string | number>) => {
        const template = DICTIONARIES[language][key] ?? DICTIONARIES.en[key] ?? key;

        if(!args) {
            return template;
        }

        return args.reduce<string>((text, arg, index) => text.split(`{{${index}}}`).join(String(arg)), template);
    }, [
        language
    ]);

    const value = useMemo(() => ({
        setLanguage,
        language,
        t
    }), [
        setLanguage,
        language,
        t
    ]);

    return <DocsI18nContext.Provider
        value={value}
    >
        {children}
    </DocsI18nContext.Provider>;
};

export const useDocsI18n = () => useContext(DocsI18nContext);
